import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Plus, FileText, Trash2 } from "lucide-react";
import type { MaterialEntry } from "@/domain/rdo/schema";
import { DialogAddMaterial } from "../DialogAddMaterial";

interface MaterialsModuleProps {
  value: MaterialEntry[];
  onChange: (materials: MaterialEntry[]) => void;
  mode: "create" | "edit" | "view";
}

export function MaterialsModule({ value, onChange, mode }: MaterialsModuleProps) {
  const [showMaterialModal, setShowMaterialModal] = useState(false);

  // Separar materiais por tipo
  const receivedMaterials = value.filter(m => m.kind === "received");
  const usedMaterials = value.filter(m => m.kind === "used");

  // Converter para formato legado esperado pelo DialogAddMaterial
  const handleAddMaterial = (materialData: any) => {
    const { type, ...data } = materialData;
    
    const newMaterial: MaterialEntry = {
      id: crypto.randomUUID(),
      kind: type as "received" | "used",
      materialId: data.materialId,
      name: data.material || data.name || "",
      unit: data.unit || "",
      qty: Number(data.quantity || data.qty || 0),
      supplierName: data.supplier,
      document: data.document,
      receivedAt: data.time,
      conformity: data.status === "conforme" ? "ok" : 
                 data.status === "recusado" ? "nok" : undefined,
      notes: data.observation || data.observations
    };

    onChange([...value, newMaterial]);
  };

  const handleDeleteMaterial = (materialId: string) => {
    onChange(value.filter(m => m.id !== materialId));
  };

  const getStatusBadge = (conformity?: "ok" | "nok") => {
    switch (conformity) {
      case "ok":
        return <Badge className="bg-green-100 text-green-800 text-xs">Conforme</Badge>;
      case "nok":
        return <Badge className="bg-red-100 text-red-800 text-xs">Recusado</Badge>;
      default:
        return null;
    }
  };

  const calculateTotals = (materials: MaterialEntry[]) => {
    const totals: Record<string, number> = {};
    materials.forEach(material => {
      if (totals[material.unit]) {
        totals[material.unit] += material.qty;
      } else {
        totals[material.unit] = material.qty;
      }
    });
    return totals;
  };

  const receivedTotals = calculateTotals(receivedMaterials);
  const usedTotals = calculateTotals(usedMaterials);

  return (
    <Card data-section="materials">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Materiais
          {mode === "edit" && (
            <Dialog open={showMaterialModal} onOpenChange={setShowMaterialModal}>
              <DialogTrigger asChild>
                <Button size="sm" className="ml-2">
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </DialogTrigger>
              <DialogAddMaterial 
                open={showMaterialModal} 
                onOpenChange={setShowMaterialModal}
                onAdd={handleAddMaterial} 
              />
            </Dialog>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="received" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="received" className="text-xs">
              Recebidos ({receivedMaterials.length})
            </TabsTrigger>
            <TabsTrigger value="used" className="text-xs">
              Utilizados ({usedMaterials.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="received" data-section="materials_received">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Material</TableHead>
                    <TableHead className="text-xs">Unid.</TableHead>
                    <TableHead className="text-xs">Qtd</TableHead>
                    <TableHead className="text-xs">Fornecedor</TableHead>
                    <TableHead className="text-xs">Documento</TableHead>
                    <TableHead className="text-xs">Hora</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Observações</TableHead>
                    {mode === "edit" && <TableHead className="text-xs">Ações</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {receivedMaterials.map((material) => (
                    <TableRow key={material.id}>
                      <TableCell className="font-medium text-xs">{material.name}</TableCell>
                      <TableCell className="text-xs">{material.unit}</TableCell>
                      <TableCell className="text-xs">{material.qty}</TableCell>
                      <TableCell className="text-xs">{material.supplierName}</TableCell>
                      <TableCell className="text-xs">{material.document}</TableCell>
                      <TableCell className="text-xs">{material.receivedAt}</TableCell>
                      <TableCell>{getStatusBadge(material.conformity)}</TableCell>
                      <TableCell className="text-xs max-w-xs">{material.notes}</TableCell>
                      {mode === "edit" && (
                        <TableCell className="text-xs text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteMaterial(material.id!)}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {receivedMaterials.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={mode === "edit" ? 9 : 8} className="text-center text-muted-foreground">
                        Nenhum material recebido
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              
              {/* Totals for Received */}
              {receivedMaterials.length > 0 && (
                <div className="mt-4 p-3 bg-muted/30 rounded-lg border">
                  <h4 className="text-xs font-semibold mb-2">Totais por Unidade:</h4>
                  <div className="flex flex-wrap gap-4">
                    {Object.entries(receivedTotals).map(([unit, total]) => (
                      <div key={unit} className="text-xs">
                        <span className="font-medium">{total} {unit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="used" data-section="materials_used">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Material</TableHead>
                    <TableHead className="text-xs">Unid.</TableHead>
                    <TableHead className="text-xs">Qtd</TableHead>
                    <TableHead className="text-xs">Local de Aplicação</TableHead>
                    <TableHead className="text-xs">Observações</TableHead>
                    {mode === "edit" && <TableHead className="text-xs">Ações</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usedMaterials.map((material) => (
                    <TableRow key={material.id}>
                      <TableCell className="font-medium text-xs">{material.name}</TableCell>
                      <TableCell className="text-xs">{material.unit}</TableCell>
                      <TableCell className="text-xs">{material.qty}</TableCell>
                      <TableCell className="text-xs">{material.location}</TableCell>
                      <TableCell className="text-xs max-w-xs">{material.notes}</TableCell>
                      {mode === "edit" && (
                        <TableCell className="text-xs text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteMaterial(material.id!)}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {usedMaterials.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={mode === "edit" ? 6 : 5} className="text-center text-muted-foreground">
                        Nenhum material utilizado
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              
              {/* Totals for Used */}
              {usedMaterials.length > 0 && (
                <div className="mt-4 p-3 bg-muted/30 rounded-lg border">
                  <h4 className="text-xs font-semibold mb-2">Totais por Unidade:</h4>
                  <div className="flex flex-wrap gap-4">
                    {Object.entries(usedTotals).map(([unit, total]) => (
                      <div key={unit} className="text-xs">
                        <span className="font-medium">{total} {unit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}